/**
 * Notification Sound Utility
 * 
 * Uses Web Audio API with automatic priming on first user interaction.
 * The AudioContext is created and resumed on the first click/touch anywhere
 * on the page, then subsequent calls to playNotificationSound() work reliably.
 */

let audioContext = null;
let primed = false;

/**
 * Prime the AudioContext on first user interaction.
 * Must be called from a click/touch/keydown event handler.
 */
function primeAudio() {
    if (primed) return;
    try {
        audioContext = new (window.AudioContext || window.webkitAudioContext)();
        // Play a silent buffer to fully unlock the context
        const buffer = audioContext.createBuffer(1, 1, 22050);
        const source = audioContext.createBufferSource();
        source.buffer = buffer;
        source.connect(audioContext.destination);
        source.start(0);
        primed = true;
        // Remove listeners once primed
        document.removeEventListener('click', primeAudio, true);
        document.removeEventListener('touchstart', primeAudio, true);
        document.removeEventListener('keydown', primeAudio, true);
    } catch {
        // Not available
    }
}

// Auto-prime on first interaction — capture phase so it fires before anything else
if (typeof document !== 'undefined') {
    document.addEventListener('click', primeAudio, true);
    document.addEventListener('touchstart', primeAudio, true);
    document.addEventListener('keydown', primeAudio, true);
}

/**
 * Play a pleasant two-tone notification chime.
 * Only works after user has interacted with the page at least once.
 */
export function playNotificationSound() {
    if (!audioContext || audioContext.state === 'closed') return;

    try {
        // Resume if suspended (e.g. after tab goes idle)
        if (audioContext.state === 'suspended') {
            audioContext.resume();
        }

        const now = audioContext.currentTime;

        // ─── Tone 1 (C5 = 523 Hz) ───────────────────────
        const osc1 = audioContext.createOscillator();
        const gain1 = audioContext.createGain();
        osc1.type = 'sine';
        osc1.frequency.setValueAtTime(523.25, now);
        gain1.gain.setValueAtTime(0.18, now);
        gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.3);
        osc1.connect(gain1);
        gain1.connect(audioContext.destination);
        osc1.start(now);
        osc1.stop(now + 0.3);

        // ─── Tone 2 (E5 = 659 Hz) — slight delay for "ding-ding" ──
        const osc2 = audioContext.createOscillator();
        const gain2 = audioContext.createGain();
        osc2.type = 'sine';
        osc2.frequency.setValueAtTime(659.25, now + 0.15);
        gain2.gain.setValueAtTime(0, now);
        gain2.gain.setValueAtTime(0.14, now + 0.15);
        gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.5);
        osc2.connect(gain2);
        gain2.connect(audioContext.destination);
        osc2.start(now + 0.15);
        osc2.stop(now + 0.5);

        // Cleanup oscillators after they finish
        osc1.onended = () => { osc1.disconnect(); gain1.disconnect(); };
        osc2.onended = () => { osc2.disconnect(); gain2.disconnect(); };
    } catch (err) {
        console.warn('[NotificationSound] Could not play:', err.message);
    }
}
